import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useServices from '../../stores/useServices';
import Breadcrumb from '../../components/Breadcrumb';
import Heading from '../../components/Heading';
import LayoutGuest from '../../components/layouts/LayoutGuest';
import { BASE_URL } from '../../routes/routeConstant';
import useCompanyProfile from '../../stores/useCompanyProfiles';
import ServiceCard from '../ServiceView/component/ServiceCard';
import useClientCompanies from '../../stores/useClientCompanies';
import Skeleton from '../../components/ui/skeleton';
import { LanguageContext } from '../../contexts/LanguageContext';

export default function AboutUsPage() {
  const { t } = useTranslation();
  const { language } =
    useContext(LanguageContext) ||
    (() => {
      throw new Error('LanguageSwitcher must be used within a LanguageProvider');
    })();
  const lang = language === 'id';
  const breadcrumbPath = [{ name: t('navigation.home'), link: BASE_URL }, { name: t('navigation.about') }];
  const { profile } = useCompanyProfile();
  const { services } = useServices();
  const { clients, fetchClients, fetchLoading, hasFetched } = useClientCompanies();

  useEffect(() => {
    if (!fetchLoading && !hasFetched) fetchClients();
  }, [hasFetched, fetchClients, fetchLoading]);

  return (
    <LayoutGuest className="max-w-screen-xl">
      <section className="flex flex-col items-center h-full w-full gap-4 p-2 my-4 md:my-8">
        <div className="flex flex-col items-center gap-1">
          <Heading>{t('navigation.about')}</Heading>
          <Breadcrumb backStatus="hidden" paths={breadcrumbPath} />
        </div>
        <div className="flex flex-col gap-8 sm:text-base text-sm w-full px-2 sm:px-4">
          <div
            key="sejarah"
            className="duration-300 animate-fade-in-up flex md:flex-row flex-col gap-8 sm:text-base text-sm w-full items-center font-medium"
          >
            <img
              src="/assets/story-image.jpg"
              className="shadow-[14px_14px_0px_0px_rgba(0,_0,_0,_0.1)] h-64 md:w-[800px] w-full object-cover rounded-lg"
              alt=""
            />
            <div className="space-y-2 w-full">
              <Heading>{t('general.story')}</Heading>
              <p>{lang ? profile?.story_id : profile?.story_en || profile?.story_id}</p>
            </div>
          </div>
          <div key="visimisi" className="flex animate-fade-in-up duration-500 md:flex-row flex-col gap-4 md:my-4">
            <div className="space-y-2 w-full sm:text-base text-sm font-medium">
              <Heading>{t('general.vision')}</Heading>
              <p>{lang ? profile?.vision_id : profile?.vision_en || profile?.vision_id}</p>
            </div>
            <div className="space-y-2 w-full sm:text-base text-sm font-medium">
              <Heading>{t('general.mission')}</Heading>
              <p>{lang ? profile?.mission_id : profile?.mission_en || profile?.mission_id}</p>
            </div>
          </div>
          <div
            key="service"
            className="flex animate-fade-in-up duration-700 flex-col gap-4 w-full sm:text-base text-sm font-medium"
          >
            <Heading>{t('footer.services.title')}</Heading>
            <div className="grid sm:grid-cols-[repeat(auto-fill,_minmax(16rem,_1fr))] grid-cols-[repeat(auto-fill,_minmax(8rem,_1fr))] gap-1 sm:gap-4">
              {services.map((srv) => (
                <ServiceCard
                  key={`service-${srv.service_id}`}
                  id={srv.service_id}
                  desc={lang ? srv.description_srv_id : srv.description_srv_en || srv.description_srv_id}
                  name={lang ? srv.name_srv_id : srv.name_srv_en || srv.name_srv_id}
                  image={srv.image_srv || '/assets/no-data-image.svg'}
                />
              ))}
            </div>
          </div>
          <div
            key="client"
            className="flex animate-fade-in-up duration-1000 flex-col gap-4 w-full sm:text-base text-sm font-medium"
          >
            <Heading>{t('general.clients')}</Heading>
            {fetchLoading ? (
              <div className="grid sm:grid-cols-[repeat(auto-fill,_minmax(16rem,_1fr))] grid-cols-[repeat(auto-fill,_minmax(8rem,_1fr))] gap-1 sm:gap-4">
                {Array.from({ length: 10 }).map(() => (
                  <Skeleton
                    key={Math.random()}
                    className="flex flex-col items-center hover-shadow p-4 justify-between gap-2 transition-all duration-200 cursor-pointer border border-border bg-white animate-scale-in rounded-lg shadow-md"
                  >
                    <Skeleton className="sm:w-24 sm:h-24 w-16 h-16 bg-neutral-200" />
                    <Skeleton className="sm:w-24 w-16 h-3 bg-neutral-200" />
                  </Skeleton>
                ))}
              </div>
            ) : (
              <div className="grid sm:grid-cols-[repeat(auto-fill,_minmax(16rem,_1fr))] grid-cols-[repeat(auto-fill,_minmax(8rem,_1fr))] gap-1 sm:gap-4">
                {clients.map((client) => (
                  <div
                    key={`${client?.partnercom_id}`}
                    className="flex flex-col items-center hover-shadow p-4 justify-between gap-2 transition-all duration-200 cursor-pointer border border-border bg-white animate-scale-in rounded-lg shadow-md"
                  >
                    <img
                      src={client?.logo_prt || '/assets/no-data-image.png'}
                      alt={client?.name_prt || 'Client'}
                      className="sm:w-24 sm:h-24 w-16 h-16 object-contain"
                    />
                    <h3 title={client?.name_prt} className=" text-xs text-wrap text-center font-semibold">
                      {client?.name_prt || ''}
                    </h3>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </LayoutGuest>
  );
}
